import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import * as TutorReducer from 'src/app/reducers/room/tutor.reducer';
import { TutorMessageSender } from 'src/features/room/components/tutor/tutor.types';
import { useGetRoomDetailQuery } from 'src/features/room/room.slice';

import { useTutorMessages } from './use-tutor-messages';
import { useTutorNudgeEvents } from './use-tutor-nudge';
import { useTutorSocketEvents } from './use-tutor-socket';

interface UseTutorQueuedActionsProps {
  roomId?: string;
  roomCode: string;
}

export const useTutorQueuedActions = ({ roomId, roomCode }: UseTutorQueuedActionsProps) => {
  const dispatch = useAppDispatch();
  const { data: roomData } = useGetRoomDetailQuery(roomCode);
  const roomDetails = roomData?.data;

  const {
    chat: {
      sessionId: tutorSessionId,
      loading: isLoading,
      restarting: isRestarting,
      disabled: isChatDisabled,
      error: activeChatError,
    },
    queuedAction,
  } = useAppSelector((state) => state.tutor);

  const { messages } = useTutorMessages();
  const { getQuestionHint, sendAskEchoMessage } = useTutorSocketEvents({ roomId, roomCode });
  const { handleReceiveNudge } = useTutorNudgeEvents({ roomId, roomCode });

  const isErrorActive = !!activeChatError;
  const isInputDisabled = isErrorActive || isLoading || isRestarting || isChatDisabled;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The timeoutRef is used to prevent sending the event twice on dev env (React strict mode)
  useEffect(() => {
    const canProcessHint = queuedAction?.type === 'hint' && !isLoading && !isRestarting && !isChatDisabled;
    const canProcessNormally = !isInputDisabled && messages.length > 0;

    if (tutorSessionId && queuedAction && (canProcessHint || (canProcessNormally && queuedAction.type !== 'hint'))) {
      const delay = queuedAction.withDelay ? 750 : 100;

      if (queuedAction.type === 'hint') {
        const { taskId, questionNo, defaultHint, hintMessage } = queuedAction.payload;

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          handleReceiveNudge(
            {
              message: hintMessage,
              preview: defaultHint,
              tutorSessionId: tutorSessionId ?? '',
            },
            'hint',
          );

          getQuestionHint({
            taskId,
            questionNo,
            roomType: roomDetails?.type ?? '',
            roomDifficulty: roomDetails?.difficulty ?? '',
            tutorOpened: false,
          });

          dispatch(TutorReducer.clearQueuedAction());
          timeoutRef.current = null;
        }, delay);
      } else if (queuedAction.type === 'ask-echo') {
        const { selectedText, taskId, surroundingText } = queuedAction.payload;

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          dispatch(
            TutorReducer.addMessage({
              id: `ask-echo-${Date.now()}`,
              message: selectedText,
              sender: TutorMessageSender.USER,
              type: 'ask-echo',
            }),
          );
          sendAskEchoMessage({
            message: selectedText,
            additionalData: {
              taskId,
              surroundingText,
            },
          });
          dispatch(TutorReducer.clearQueuedAction());
          timeoutRef.current = null;
        }, delay);
      }
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [
    tutorSessionId,
    messages,
    queuedAction,
    isInputDisabled,
    dispatch,
    roomDetails?.difficulty,
    roomDetails?.type,
    getQuestionHint,
    sendAskEchoMessage,
    handleReceiveNudge,
    isLoading,
    isRestarting,
    isChatDisabled,
  ]);

  return {};
};
